import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import { TextInput, TextareaInput, CheckboxInput, SelectInput } from '@/components/common/FormField'
import api from '@/services/api'
import toast from 'react-hot-toast'

const DEPT_TYPE_OPTIONS = [
  { value: 'kafedra', label: 'Кафедра' },
  { value: 'otdel', label: 'Отдел' },
]

const INITIAL = {
  name: '', short_name: '', head_name: '', description: '', department_type: 'kafedra', is_active: true,
}

export default function DepartmentModal({ open, onClose, department = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const isEdit = !!department

  useEffect(() => {
    if (open) {
      setForm(department ? {
        name: department.name || '',
        short_name: department.short_name || '',
        head_name: department.head_name || '',
        description: department.description || '',
        department_type: department.department_type || 'kafedra',
        is_active: department.is_active ?? true,
      } : INITIAL)
      setErrors({})
    }
  }, [open, department])

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: val }))
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Обязательное поле'
    if (form.name.length > 255) e.name = 'Максимум 255 символов'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      if (isEdit) {
        await api.patch(`/departments/departments/${department.id}/`, form)
        toast.success('Отдел обновлён')
      } else {
        await api.post('/departments/departments/', form)
        toast.success('Отдел создан')
      }
      qc.invalidateQueries(['departments-full'])
      qc.invalidateQueries(['departments'])
      qc.invalidateQueries(['departments-hierarchy'])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data?.name) setErrors({ name: Array.isArray(data.name) ? data.name[0] : data.name })
      else toast.error(data?.detail || 'Ошибка сохранения')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать отдел' : 'Добавить отдел'}
      onSubmit={handleSubmit}
      loading={loading}
    >
      <SelectInput
        label="Тип"
        value={form.department_type} onChange={set('department_type')}
        options={DEPT_TYPE_OPTIONS}
      />
      <TextInput
        label="Название" required
        value={form.name} onChange={set('name')}
        error={errors.name}
        placeholder="Учебный отдел"
      />
      <TextInput
        label="Короткое название"
        value={form.short_name} onChange={set('short_name')}
        placeholder="УО"
      />
      <TextInput
        label="Руководитель"
        value={form.head_name} onChange={set('head_name')}
        placeholder="ФИО"
      />
      <TextareaInput
        label="Описание"
        value={form.description} onChange={set('description')}
        rows={2}
      />
      <CheckboxInput
        label="Активен"
        hint="Неактивные отделы скрыты в списках"
        checked={form.is_active}
        onChange={set('is_active')}
      />
    </ModalForm>
  )
}
